import { useEffect, useState } from "react";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCrmMeta } from "@/hooks/useCrmMeta";
import { CHANNELS, STAGES } from "@/lib/constants";
import { categoryOptions, servicesInCategory } from "@/lib/categories";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/crm/Badges";
import { Chart, FirstResponse, Funnel, KpiGrid, PeriodChart } from "@/components/crm/dashboard/DashboardWidgets";

const RANGES = [["hoy", "Hoy"], ["7d", "7 días"], ["30d", "30 días"], ["todo", "Todo"], ["custom", "Rango"]];

const Pick = ({ value, onChange, all, options, testid, width = "w-40" }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={`h-9 ${width} rounded-xl bg-white text-sm`} data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{all}</SelectItem>
      {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

export default function DashboardPage() {
  const { user } = useAuth();
  const { services, sellers, categories } = useCrmMeta();
  const [range, setRange] = useState("todo");
  const [dates, setDates] = useState({ date_from: "", date_to: "" });
  const [f, setF] = useState({ seller_id: "all", channel: "all", category_id: "all", service_id: "all", stage: "all" });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    if (range === "custom" && (!dates.date_from || !dates.date_to)) return;
    const params = { range, tz_offset: new Date().getTimezoneOffset(), ...(range === "custom" ? dates : {}),
      ...Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "all")) };
    setError("");
    api.get("/dashboard", { params }).then((r) => setData(r.data)).catch((e) => setError(errorMessage(e)));
  }, [range, dates, f]);

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Dashboard" subtitle={user.role === "vendedor" ? "Resumen de tus leads" : "Resumen comercial del centro"}>
        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1" data-testid="dashboard-range">
          {RANGES.map(([k, l]) => (
            <button key={k} onClick={() => setRange(k)} data-testid={`range-${k}`}
                    className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${range === k ? "bg-[#0B2545] text-white" : "text-slate-500 hover:text-[#0B2545]"}`}>{l}</button>
          ))}
        </div>
      </PageHeader>
      <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="dashboard-filters">
        {range === "custom" && (
          <>
            <Input type="date" value={dates.date_from} onChange={(e) => setDates((d) => ({ ...d, date_from: e.target.value }))} className="h-9 w-40 rounded-xl" data-testid="dashboard-date-from" />
            <Input type="date" value={dates.date_to} onChange={(e) => setDates((d) => ({ ...d, date_to: e.target.value }))} className="h-9 w-40 rounded-xl" data-testid="dashboard-date-to" />
          </>
        )}
        {user.role !== "vendedor" && <Pick value={f.seller_id} onChange={set("seller_id")} all="Todos los vendedores" options={sellers.map((s) => [s.id, s.name])} testid="dashboard-filter-seller" />}
        <Pick value={f.channel} onChange={set("channel")} all="Todos los canales" options={CHANNELS.map((c) => [c, c])} testid="dashboard-filter-channel" />
        <Pick value={f.category_id} onChange={(v) => setF((x) => ({ ...x, category_id: v, service_id: "all" }))} all="Todas las categorías"
              options={categoryOptions(categories)} testid="dashboard-filter-category" width="w-56" />
        <Pick value={f.service_id} onChange={set("service_id")} all="Todos los servicios" options={servicesInCategory(services, f.category_id).map((s) => [s.id, s.name])} testid="dashboard-filter-service" width="w-52" />
        <Pick value={f.stage} onChange={set("stage")} all="Todos los estados" options={STAGES.map((s) => [s, s])} testid="dashboard-filter-stage" />
      </div>
      {error && <p className="mb-4 rounded-xl bg-rose-50 px-4 py-2 text-sm text-rose-700" data-testid="dashboard-error">{error}</p>}
      <KpiGrid data={data} />
      <div className="mt-6 grid gap-6 xl:grid-cols-3">
        <PeriodChart data={data?.by_period} />
        <Funnel data={data?.funnel} />
        <FirstResponse frt={data?.frt} />
        {data?.by_seller && <Chart title="Leads por vendedor" data={data.by_seller} testid="chart-by-seller" />}
        <Chart title="Leads por canal" data={data?.by_channel} testid="chart-by-channel" />
        <Chart title="Leads por servicio" data={data?.by_service} testid="chart-by-service" />
      </div>
    </div>
  );
}
